/**
 * Which configuration panel the client is actually looking at.
 *
 * Every panel is mounted at once and the inactive ones are only hidden with
 * `display: none`, so every line's claim effect runs simultaneously. With
 * two lines of the SAME product — residential and commercial staging, two editing
 * lines — they share one FileAPI scope, and both would claim the same freshly
 * uploaded files: each then filters the other's claim out of its own listing, both
 * show nothing, and the writes chase each other until React gives up with
 * "Maximum update depth exceeded" and the whole flow lands on the error boundary.
 * Reproduced on dev; it is not a race that sometimes resolves.
 *
 * So claiming is narrowed to the visible panel. That is also what the client
 * means: files land on the tab they dropped them into, not on whichever line
 * happens to be first in the cart. A hidden line keeps everything it already
 * owns — the claim is add-only and nothing here takes anything away.
 *
 * Defaults to `true` so a caller outside the configuration step (the product
 * drawer, a test rendering a module directly) behaves exactly as before.
 */

import React, { createContext, useContext } from 'react'

const ConfigPanelActiveContext = createContext(true)

export const ConfigPanelActiveProvider: React.FC<{ isActive: boolean, children: React.ReactNode }> =
  ({ isActive, children }) => (
    <ConfigPanelActiveContext.Provider value={isActive}>
      {children}
    </ConfigPanelActiveContext.Provider>
  )

/** Whether this panel may claim newly uploaded files onto its line. */
export const useCanClaimSourceVisuals = (): boolean => useContext(ConfigPanelActiveContext)

/** Whether this panel is the one the client is looking at. */
export const useIsConfigPanelActive = (): boolean => useContext(ConfigPanelActiveContext)
